import { useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  Clock3,
  History,
  Inbox,
  LifeBuoy,
  Loader2,
  Mail,
  Phone,
  RefreshCw,
  Repeat2,
  ShieldAlert,
  UserCheck,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useToast } from '@/hooks/use-toast';
import type {
  LeadEnquiryAgeState,
  LeadEnquiryAssignee,
  LeadEnquiryQueueCountResponse,
  LeadEnquiryQueueItem,
  LeadEnquiryQueueResponse,
} from '../../types';

export const LEAD_ENQUIRY_QUEUE_QUERY_KEY = '/api/pipeline/enquiries/queue';
export const LEAD_ENQUIRY_QUEUE_COUNT_QUERY_KEY = '/api/pipeline/enquiries/queue/count';

const QUEUE_PAGE_LIMIT = 100;
type EnquiryQueueScope = 'unassigned' | 'assigned_to_me';

const SEVERITY_ORDER: Record<LeadEnquiryAgeState, number> = {
  needs_attention: 0,
  waiting: 1,
  new: 2,
};

function dateTimestamp(value: string): number {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
}

function ageSortTimestamp(item: LeadEnquiryQueueItem): number {
  switch (item.ageState) {
    case 'needs_attention':
      return dateTimestamp(item.needsAttentionAt);
    case 'waiting':
      return dateTimestamp(item.waitingAt);
    case 'new':
      return dateTimestamp(item.receivedAt);
  }
}

/** Red first, then amber, then new; oldest relevant threshold first. */
export function sortEnquiryQueueItems(items: LeadEnquiryQueueItem[]): LeadEnquiryQueueItem[] {
  return [...items].sort((left, right) => {
    const severityDifference = SEVERITY_ORDER[left.ageState] - SEVERITY_ORDER[right.ageState];

    if (severityDifference !== 0) return severityDifference;

    const ageDifference = ageSortTimestamp(left) - ageSortTimestamp(right);
    if (ageDifference !== 0) return ageDifference;

    return left.enquiryId.localeCompare(right.enquiryId);
  });
}

export function getEnquiryAgeLabel(ageState: LeadEnquiryAgeState): string {
  switch (ageState) {
    case 'needs_attention':
      return 'Needs attention';
    case 'waiting':
      return 'Waiting';
    case 'new':
      return 'New';
  }
}

export function getEnquiryAgeBadgeClasses(ageState: LeadEnquiryAgeState): string {
  switch (ageState) {
    case 'needs_attention':
      return 'border-red-200 bg-red-100 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300';
    case 'waiting':
      return 'border-amber-200 bg-amber-100 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300';
    case 'new':
      return 'border-blue-200 bg-blue-100 text-blue-800 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300';
  }
}

interface ApiErrorBody {
  error?: string;
  message?: string;
  currentAssignee?: LeadEnquiryAssignee | null;
}

export class LeadEnquiryClaimConflictError extends Error {
  readonly currentAssignee: LeadEnquiryAssignee | null;

  constructor(message: string, currentAssignee: LeadEnquiryAssignee | null) {
    super(message);
    this.name = 'LeadEnquiryClaimConflictError';
    this.currentAssignee = currentAssignee;
  }
}

async function readJsonResponse<T>(response: Response): Promise<T> {
  const body = (await response.json().catch(() => ({}))) as ApiErrorBody | T;

  if (response.ok) return body as T;

  const errorBody = body as ApiErrorBody;
  if (response.status === 409 && errorBody.error === 'claim_conflict') {
    throw new LeadEnquiryClaimConflictError(
      errorBody.message || 'This enquiry has already been claimed.',
      errorBody.currentAssignee ?? null
    );
  }

  throw new Error(errorBody.message || `Request failed with status ${response.status}`);
}

export async function fetchEnquiryQueuePage(
  scope: EnquiryQueueScope,
  offset = 0
): Promise<LeadEnquiryQueueResponse> {
  const params = new URLSearchParams({
    scope,
    limit: String(QUEUE_PAGE_LIMIT),
    offset: String(offset),
  });
  const response = await fetch(`${LEAD_ENQUIRY_QUEUE_QUERY_KEY}?${params.toString()}`, {
    credentials: 'include',
  });

  return readJsonResponse<LeadEnquiryQueueResponse>(response);
}

export async function fetchUnassignedEnquiryQueue(): Promise<LeadEnquiryQueueResponse> {
  return fetchEnquiryQueuePage('unassigned');
}

export async function fetchUnassignedEnquiryCount(): Promise<LeadEnquiryQueueCountResponse> {
  const response = await fetch(LEAD_ENQUIRY_QUEUE_COUNT_QUERY_KEY, {
    credentials: 'include',
  });

  return readJsonResponse<LeadEnquiryQueueCountResponse>(response);
}

export async function claimLeadEnquiry(enquiryId: string): Promise<void> {
  const response = await fetch(`/api/pipeline/enquiries/${encodeURIComponent(enquiryId)}/claim`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });

  await readJsonResponse(response);
}

function formatReceivedAge(receivedAt: string): string {
  const receivedDate = new Date(receivedAt);
  if (Number.isNaN(receivedDate.getTime())) return 'Received time unavailable';
  return `Received ${formatDistanceToNow(receivedDate, { addSuffix: true })}`;
}

function QueueSummary({ counts }: { counts: LeadEnquiryQueueCountResponse['data'] }) {
  return (
    <div className="grid grid-cols-3 gap-2 px-6 py-3 border-b bg-slate-50/60 dark:bg-slate-900/40">
      <div className="rounded-md border border-red-200 bg-red-50 p-2 text-center dark:border-red-900 dark:bg-red-950/30">
        <div className="text-lg font-semibold tabular-nums text-red-700 dark:text-red-300">
          {counts.needsAttention}
        </div>
        <div className="text-[10px] font-medium text-red-600 dark:text-red-400">
          Needs attention
        </div>
      </div>
      <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-center dark:border-amber-900 dark:bg-amber-950/30">
        <div className="text-lg font-semibold tabular-nums text-amber-700 dark:text-amber-300">
          {counts.waiting}
        </div>
        <div className="text-[10px] font-medium text-amber-600 dark:text-amber-400">Waiting</div>
      </div>
      <div className="rounded-md border border-blue-200 bg-blue-50 p-2 text-center dark:border-blue-900 dark:bg-blue-950/30">
        <div className="text-lg font-semibold tabular-nums text-blue-700 dark:text-blue-300">
          {counts.new}
        </div>
        <div className="text-[10px] font-medium text-blue-600 dark:text-blue-400">New</div>
      </div>
    </div>
  );
}

function ContextBadges({ item }: { item: LeadEnquiryQueueItem }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {item.classification === 'repeat_enquiry' && (
        <Badge variant="outline" className="gap-1 text-[10px]">
          <Repeat2 className="h-3 w-3" />
          Repeat enquiry
        </Badge>
      )}
      {(item.classification === 'identity_conflict' || item.identityReviewRequired) && (
        <Badge
          variant="outline"
          className="gap-1 border-violet-200 bg-violet-50 text-[10px] text-violet-700 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-300"
        >
          <ShieldAlert className="h-3 w-3" />
          Identity review
        </Badge>
      )}
      {item.rescued && (
        <Badge variant="outline" className="gap-1 text-[10px]">
          <LifeBuoy className="h-3 w-3" />
          Rescued
        </Badge>
      )}
    </div>
  );
}

/**
 * Persistent shared queue surface. Count stays live while the sheet is closed;
 * the full list is fetched only when staff open it.
 */
export function UnassignedEnquiryQueue() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<EnquiryQueueScope>('unassigned');

  const countQuery = useQuery<LeadEnquiryQueueCountResponse>({
    queryKey: [LEAD_ENQUIRY_QUEUE_COUNT_QUERY_KEY],
    queryFn: fetchUnassignedEnquiryCount,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  const queueQuery = useInfiniteQuery({
    queryKey: [LEAD_ENQUIRY_QUEUE_QUERY_KEY, 'infinite', { scope }],
    queryFn: ({ pageParam }) => fetchEnquiryQueuePage(scope, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.data.offset + lastPage.data.items.length;
      return loaded < lastPage.data.total && lastPage.data.items.length > 0 ? loaded : undefined;
    },
    enabled: open,
    staleTime: 15_000,
    refetchInterval: open ? 30_000 : false,
  });

  const items = useMemo(
    () => sortEnquiryQueueItems(queueQuery.data?.pages.flatMap((page) => page.data.items) ?? []),
    [queueQuery.data?.pages]
  );
  const unassignedTotal = countQuery.data?.data.total ?? 0;
  const visibleTotal =
    scope === 'unassigned'
      ? (countQuery.data?.data.total ?? queueQuery.data?.pages[0]?.data.total ?? 0)
      : (queueQuery.data?.pages[0]?.data.total ?? 0);

  const claimMutation = useMutation({
    mutationFn: claimLeadEnquiry,
    onSuccess: async () => {
      toast({
        title: 'Enquiry claimed',
        description: 'It is now assigned to you. Customer contact is still recorded separately.',
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [LEAD_ENQUIRY_QUEUE_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [LEAD_ENQUIRY_QUEUE_COUNT_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: ['/api/pipeline-stages/data'] }),
      ]);
    },
    onError: async (error: Error) => {
      if (error instanceof LeadEnquiryClaimConflictError) {
        toast({
          title: 'Already claimed',
          description: error.currentAssignee?.name
            ? `${error.currentAssignee.name} claimed this enquiry first. The queue has been refreshed.`
            : 'Another team member claimed this enquiry first. The queue has been refreshed.',
          variant: 'destructive',
        });
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [LEAD_ENQUIRY_QUEUE_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [LEAD_ENQUIRY_QUEUE_COUNT_QUERY_KEY] }),
        ]);
        return;
      }

      toast({
        title: 'Could not claim enquiry',
        description: error.message || 'Please refresh the queue and try again.',
        variant: 'destructive',
      });
    },
  });

  const triggerClasses = countQuery.data?.data.needsAttention
    ? 'border-red-200 text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/30'
    : countQuery.data?.data.waiting
      ? 'border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-900 dark:text-amber-300 dark:hover:bg-amber-950/30'
      : '';

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`relative flex-shrink-0 gap-1.5 ${triggerClasses}`}
          title={
            countQuery.isError
              ? 'Unassigned enquiry count is unavailable'
              : 'View unassigned enquiries'
          }
        >
          <Inbox className="h-4 w-4" />
          <span className="hidden sm:inline">Unassigned</span>
          <Badge
            variant={
              countQuery.isError ? 'destructive' : unassignedTotal > 0 ? 'default' : 'secondary'
            }
            className="h-5 min-w-5 justify-center px-1.5 text-[10px] tabular-nums"
            aria-label={
              countQuery.isError
                ? 'Unassigned enquiry count unavailable'
                : `${unassignedTotal} unassigned enquiries`
            }
          >
            {countQuery.isError ? '!' : countQuery.isLoading ? '…' : unassignedTotal}
          </Badge>
        </Button>
      </SheetTrigger>

      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Inbox className="h-5 w-5 text-slate-500" />
            {scope === 'unassigned' ? 'Unassigned enquiries' : 'Assigned to me'}
            {visibleTotal > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs tabular-nums">
                {visibleTotal}
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            {scope === 'unassigned'
              ? 'Claim an enquiry to take ownership. Assignment does not mean the customer has been contacted.'
              : 'Your open assigned enquiries remain here until confirmed customer contact is recorded.'}
          </SheetDescription>
          <div className="flex gap-2 pt-2" role="tablist" aria-label="Enquiry queue scope">
            <Button
              type="button"
              size="sm"
              variant={scope === 'unassigned' ? 'default' : 'outline'}
              role="tab"
              aria-selected={scope === 'unassigned'}
              onClick={() => setScope('unassigned')}
            >
              Unassigned
            </Button>
            <Button
              type="button"
              size="sm"
              variant={scope === 'assigned_to_me' ? 'default' : 'outline'}
              role="tab"
              aria-selected={scope === 'assigned_to_me'}
              onClick={() => setScope('assigned_to_me')}
            >
              Assigned to me
            </Button>
          </div>
        </SheetHeader>

        {scope === 'unassigned' && countQuery.data && (
          <QueueSummary counts={countQuery.data.data} />
        )}

        <ScrollArea className="flex-1 min-h-0">
          <div className="px-6 py-4 space-y-3">
            {(queueQuery.isLoading || (queueQuery.isFetching && items.length === 0)) && (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mb-3" />
                <p className="text-sm">Loading unassigned enquiries…</p>
              </div>
            )}

            {queueQuery.isError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center dark:border-red-900 dark:bg-red-950/30">
                <AlertTriangle className="mx-auto mb-2 h-6 w-6 text-red-500" />
                <p className="text-sm font-medium text-red-800 dark:text-red-300">
                  The queue could not be loaded
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 gap-2"
                  onClick={() => queueQuery.refetch()}
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Try again
                </Button>
              </div>
            )}

            {!queueQuery.isLoading && !queueQuery.isError && items.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <UserCheck className="h-10 w-10 mb-3 opacity-40" />
                <p className="text-sm font-medium">No unassigned enquiries</p>
                <p className="text-xs mt-1">New enquiries will appear here automatically.</p>
              </div>
            )}

            {items.map((item) => {
              const claimPending =
                claimMutation.isPending && claimMutation.variables === item.enquiryId;

              return (
                <article
                  key={item.enquiryId}
                  className="rounded-lg border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant="outline"
                          className={`gap-1 text-[10px] ${getEnquiryAgeBadgeClasses(item.ageState)}`}
                        >
                          {item.ageState === 'needs_attention' ? (
                            <AlertTriangle className="h-3 w-3" />
                          ) : (
                            <Clock3 className="h-3 w-3" />
                          )}
                          {getEnquiryAgeLabel(item.ageState)}
                        </Badge>
                        <span className="text-[11px] text-muted-foreground">
                          {formatReceivedAge(item.receivedAt)}
                        </span>
                      </div>

                      <div>
                        {item.customerId !== null ? (
                          <a
                            href={`/customers/${item.customerId}`}
                            className="font-medium text-slate-900 hover:text-blue-600 hover:underline dark:text-slate-100 dark:hover:text-blue-400"
                          >
                            {item.customerName || 'Customer'}
                          </a>
                        ) : (
                          <span className="font-medium text-muted-foreground">
                            Customer record unavailable
                          </span>
                        )}
                        {item.customerEmail && (
                          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                            <Mail className="h-3 w-3 shrink-0" />
                            <span className="truncate">{item.customerEmail}</span>
                          </div>
                        )}
                        {item.customerPhone && (
                          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                            <Phone className="h-3 w-3 shrink-0" />
                            <span>{item.customerPhone}</span>
                          </div>
                        )}
                      </div>

                      <ContextBadges item={item} />

                      <div className="grid gap-1 rounded-md bg-slate-50 p-2 text-[11px] text-slate-600 dark:bg-slate-800/60 dark:text-slate-300">
                        <div className="flex items-center justify-between gap-3">
                          <span>Assigned</span>
                          <span className="font-medium">
                            {scope === 'assigned_to_me'
                              ? item.assignee?.name || 'Assigned to you'
                              : 'Unassigned'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span>Customer contacted</span>
                          <span className="font-medium">Not yet</span>
                        </div>
                      </div>
                    </div>

                    {scope === 'unassigned' && (
                      <Button
                        size="sm"
                        className="shrink-0"
                        disabled={claimMutation.isPending}
                        onClick={() => claimMutation.mutate(item.enquiryId)}
                      >
                        {claimPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Claim'}
                      </Button>
                    )}
                  </div>
                </article>
              );
            })}

            {queueQuery.hasNextPage && (
              <div className="flex flex-col items-center justify-center gap-2 py-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-2">
                  <History className="h-3.5 w-3.5" />
                  Showing {items.length} of {visibleTotal} enquiries
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={queueQuery.isFetchingNextPage}
                  onClick={() => queueQuery.fetchNextPage()}
                >
                  {queueQuery.isFetchingNextPage ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  Load more
                </Button>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
